import React from 'react';
import Modal from '../common/Modal';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

/**
 * Import Preview Modal Component (EN-21290)
 * REQ #4: CSV import preview/dry-run
 *
 * Displays a summary of what will change before committing CSV import
 */
export default function ImportPreviewModal({ open, data, onConfirm, onCancel, importMode, loading = false }) {
    if (!data) return null;

    const hasWarnings = data.warnings && data.warnings.length > 0;
    const hasChanges = data.shiftsAssigned > 0 || data.shiftsCleared > 0;
    const isPartialUpdate = importMode === 'PARTIAL_UPDATE';

    // Calculate display values based on new API response
    const existingMatched = data.existingMatched ?? data.rowsProcessed; // Fallback for old API
    const newPersonnel = data.newPersonnel ?? 0;
    const totalRows = data.rowsProcessed;

    // Detailed explanation for tooltip
    const getDetailedExplanation = () => {
        if (isPartialUpdate) {
            return (
                <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Partial Update Scenarios:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>• Fewer rows in CSV:</strong> Only the personnel listed in your CSV will be updated. All other existing personnel remain unchanged.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>• Same number of rows:</strong> Only matching personnel will be updated with the data from your CSV file.
                    </Typography>
                    <Typography variant="body2">
                        <strong>• Extra rows in CSV:</strong> Only existing personnel are updated. New personnel entries in the CSV will be <strong>ignored</strong> (not created).
                    </Typography>
                </Box>
            );
        } else {
            return (
                <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'warning.main' }}>
                        Full Replace Scenarios (Creates New Personnel):
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>• Fewer rows in CSV:</strong> Personnel in your CSV will be updated. Personnel NOT in the CSV remain unchanged (<strong>NOT deleted</strong>).
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>• Same number of rows:</strong> Matching personnel will be updated with the data from your CSV file. No deletions occur.
                    </Typography>
                    <Typography variant="body2">
                        <strong>• Extra rows in CSV:</strong> Existing personnel will be updated AND new personnel will be <strong>created</strong> from the additional rows in your CSV.
                    </Typography>
                </Box>
            );
        }
    };

    const modalContent = (
        <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Review Changes Before Import
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Import Mode Explanation */}
                <Alert
                    severity={isPartialUpdate ? "info" : "warning"}
                    icon={isPartialUpdate ? <InfoIcon /> : <WarningIcon />}
                    sx={{ mb: 3 }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                <strong>Import Mode: {isPartialUpdate ? 'Partial Update' : 'Full Replace'}</strong>
                            </Typography>
                            <Typography variant="body2">
                                {isPartialUpdate
                                    ? 'Only updates personnel present in your CSV file. New entries are ignored.'
                                    : 'Updates existing personnel AND creates new personnel from your CSV. Does NOT delete personnel not in CSV.'}
                            </Typography>
                        </Box>
                        <Tooltip
                            title={getDetailedExplanation()}
                            arrow
                            placement="right"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: 'background.paper',
                                        color: 'text.primary',
                                        boxShadow: 3,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        maxWidth: 450,
                                        '& .MuiTooltip-arrow': {
                                            color: 'background.paper',
                                            '&::before': {
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            <IconButton
                                size="small"
                                aria-label="Help: Row count explanation"
                                sx={{
                                    ml: 1,
                                    color: isPartialUpdate ? 'info.main' : 'warning.main',
                                    '&:hover': {
                                        backgroundColor: isPartialUpdate ? 'info.light' : 'warning.light',
                                        opacity: 0.1
                                    }
                                }}
                            >
                                <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Alert>

                {/* Summary Section */}
                <Box sx={{ my: 3 }}>
                    <Stack spacing={2}>
                        <Box display="flex" alignItems="center">
                            <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
                            <Box>
                                {isPartialUpdate ? (
                                    <>
                                        <Typography variant="body1">
                                            <strong>{existingMatched}</strong> personnel will be updated
                                            {totalRows > existingMatched && (
                                                <> (from <strong>{totalRows}</strong> rows in CSV)</>
                                            )}
                                        </Typography>
                                        {totalRows > existingMatched && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {totalRows - existingMatched} new {totalRows - existingMatched === 1 ? 'entry' : 'entries'} in CSV will be ignored (not created).
                                            </Typography>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="body1">
                                            <strong>{totalRows}</strong> rows will be processed
                                            {newPersonnel > 0 && (
                                                <> (<strong>{existingMatched}</strong> {existingMatched === 1 ? 'update' : 'updates'} + <strong>{newPersonnel}</strong> {newPersonnel === 1 ? 'create' : 'creates'})</>
                                            )}
                                        </Typography>
                                        {newPersonnel > 0 && (
                                            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                                                ⚠️ {newPersonnel} new personnel will be created from your CSV.
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>

                        {data.shiftsAssigned > 0 && (
                            <Box display="flex" alignItems="center">
                                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                <Typography variant="body1">
                                    <strong>{data.shiftsAssigned}</strong> personnel will have shifts assigned
                                </Typography>
                            </Box>
                        )}

                        {data.shiftsCleared > 0 && (
                            <Box display="flex" alignItems="center" color="warning.main">
                                <WarningIcon color="warning" sx={{ mr: 1 }} />
                                <Typography variant="body1">
                                    <strong>{data.shiftsCleared}</strong> personnel will have shifts CLEARED
                                </Typography>
                            </Box>
                        )}

                        {!hasChanges && (
                            <Typography variant="body2" color="text.secondary">
                                No shift changes detected in this import.
                            </Typography>
                        )}
                    </Stack>
                </Box>

                {/* Warnings Section */}
                {hasWarnings && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                <WarningIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
                                {data.warnings.length} Warning{data.warnings.length > 1 ? 's' : ''}:
                            </Typography>
                            <List dense sx={{ mt: 1 }}>
                                {data.warnings.slice(0, 10).map((warning, index) => (
                                    <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                                        <ListItemText
                                            primary={warning}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                        />
                                    </ListItem>
                                ))}
                                {data.warnings.length > 10 && (
                                    <ListItem sx={{ py: 0.5, pl: 0 }}>
                                        <ListItemText
                                            primary={`... and ${data.warnings.length - 10} more warnings`}
                                            primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Alert>
                    </>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button
                        onClick={onCancel}
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                    >
                        {loading ? 'Importing...' : 'Confirm Import'}
                    </Button>
                </Box>
            </Box>
    );

    return (
        <Modal
            open={open}
            handleClose={onCancel}
            size="large"
            title="Import Preview"
            content={modalContent}
        />
    );
}
